import { Job, IndeedFields, IndeedHiringTimeline, IndeedJobType, IndeedExpectedHoursShowBy, IndeedShowPayBy, IndeedPayRate } from '../types';

export const indeedHiringTimelineOptions = [
  { label: '1 to 3 days', value: '1 to 3 days' as const },
  { label: '3 to 7 days', value: '3 to 7 days' as const },
  { label: '1 to 2 weeks', value: '1 to 2 weeks' as const },
  { label: '2 to 4 weeks', value: '2 to 4 weeks' as const },
  { label: 'More than 4 weeks', value: 'More than 4 weeks' as const },
] satisfies ReadonlyArray<{ label: string; value: IndeedHiringTimeline }>;

export const indeedJobTypeOptions = [
  { label: 'Full-time', value: 'Full-time' as const },
  { label: 'Contract', value: 'Contract' as const },
  { label: 'Temporary', value: 'Temporary' as const },
  { label: 'Part-time', value: 'Part-time' as const },
] satisfies ReadonlyArray<{ label: string; value: IndeedJobType }>;

export const indeedExpectedHoursShowByOptions = [
  { label: 'Fixed hours', value: 'Fixed hours' as const },
  { label: 'Range', value: 'Range' as const },
  { label: 'Maximum', value: 'Maximum' as const },
  { label: 'Minimum', value: 'Minimum' as const },
] satisfies ReadonlyArray<{ label: string; value: IndeedExpectedHoursShowBy }>;

export const indeedJobTypeIncludesPartTime = (jobTypes?: IndeedJobType[]): boolean => {
  return Array.isArray(jobTypes) && jobTypes.includes('Part-time');
};

export const indeedShowPayByOptions = [
  { label: 'Range', value: 'Range' as const },
  { label: 'Starting amount', value: 'Starting amount' as const },
  { label: 'Maximum amount', value: 'Maximum amount' as const },
  { label: 'Exact amount', value: 'Exact amount' as const },
] satisfies ReadonlyArray<{ label: string; value: IndeedShowPayBy }>;

export const indeedPayRateOptions = [
  { label: 'per hour', value: 'per hour' as const },
  { label: 'per day', value: 'per day' as const },
  { label: 'per week', value: 'per week' as const },
  { label: 'per month', value: 'per month' as const },
  { label: 'per year', value: 'per year' as const },
] satisfies ReadonlyArray<{ label: string; value: IndeedPayRate }>;

export const indeedBenefitOptions = [
  '401(k)',
  '401(k) matching',
  '403(b)',
  'AD&D insurance',
  'Cell phone reimbursement',
  'Commuter assistance',
  'Company car',
  'Dental insurance',
  'Disability insurance',
  'Employee assistance program',
  'Employee discount',
  'Employee stock purchase plan',
  'Flexible schedule',
  'Flexible spending account',
  'Food provided',
  'Free parking',
  'Fuel card',
  'Health insurance',
  'Health savings account',
  'Housing allowance',
  'Life insurance',
  'Mileage reimbursement',
  'On-site gym',
  'On-the-job training',
  'Opportunities for advancement',
  'Overtime pay',
  'Paid holidays',
  'Paid parental leave',
  'Paid sick time',
  'Paid time off',
  'Paid training',
  'Parental leave',
  'Prescription drug insurance',
  'Professional development assistance',
  'Profit sharing',
  'Referral program',
  'Relocation assistance',
  'Retirement plan',
  'Safety equipment provided',
  'Stock options',
  'Tools provided',
  'Tuition reimbursement',
  'Uniform allowance',
  'Visa sponsorship',
  'Vision insurance',
  'Wellness program',
] as const;

export const getIndeedBenefitPickerOptions = (selected?: string[]): string[] => {
  const options = new Set<string>(indeedBenefitOptions);

  for (const benefit of selected || []) {
    const trimmed = benefit.trim();

    if (trimmed) {
      options.add(trimmed);
    }
  }

  return Array.from(options).sort((left, right) => left.localeCompare(right));
};

export const indeedPayUsesMinimumAndMaximum = (showPayBy?: IndeedShowPayBy): boolean => {
  return !showPayBy || showPayBy === 'Range';
};

export const indeedPayUsesAmount = (showPayBy?: IndeedShowPayBy): boolean => {
  return (
    showPayBy === 'Starting amount' || showPayBy === 'Exact amount' || showPayBy === 'Maximum amount'
  );
};

export const indeedJobLocationTypeOptions = [
  { label: 'In person', value: 'In person' as const },
  { label: 'Fully remote: no on-site work required', value: 'Fully remote: no on-site work required' as const },
  { label: 'Hybrid: some on-site work required', value: 'Hybrid: some on-site work required' as const },
  { label: 'On the road', value: 'On the road' as const },
] as const;

export const indeedJobLocationTypeShowsRemoteResidenceQuestion = (
  locationType?: IndeedFields['jobLocationType'],
): boolean => {
  return locationType === 'Fully remote: no on-site work required';
};

export const indeedShowsJobLocation = (
  locationType?: IndeedFields['jobLocationType'],
  requireRemoteResidence?: boolean,
): boolean => {
  if (!locationType) {
    return false;
  }

  if (indeedJobLocationTypeShowsRemoteResidenceQuestion(locationType)) {
    return requireRemoteResidence === true;
  }

  return true;
};

export const indeedJobLocationTypeShowsRelocate = (
  locationType?: IndeedFields['jobLocationType'],
  requireRemoteResidence?: boolean,
): boolean => {
  if (!locationType) {
    return false;
  }

  if (indeedJobLocationTypeShowsRemoteResidenceQuestion(locationType)) {
    return requireRemoteResidence === true;
  }

  return true;
};

export const indeedJobLocationFieldLabel = (
  locationType?: IndeedFields['jobLocationType'],
  requireRemoteResidence?: boolean,
): string => {
  if (indeedJobLocationTypeShowsRemoteResidenceQuestion(locationType) && requireRemoteResidence === true) {
    return 'In what location must employees reside?';
  }

  if (locationType === 'On the road') {
    return 'What is the operating area for this job?';
  }

  return 'What is the job location?';
};

export const indeedLocationTypeToTestId: Record<IndeedFields['jobLocationType'] & string, string> = {
  'In person': 'PRECISE_OR_GENERAL',
  'Fully remote: no on-site work required': 'REMOTE_WORK_FROM_HOME',
  'Hybrid: some on-site work required': 'HYBRID_REMOTE',
  'On the road': 'ON_THE_ROAD',
};

const mapLocationTypeToIndeedLocationType = (
  locationType?: string,
): IndeedFields['jobLocationType'] | undefined => {
  switch (locationType) {
    case 'Remote':
      return 'Fully remote: no on-site work required';
    case 'Hybrid':
      return 'Hybrid: some on-site work required';
    case 'OnSite':
      return 'In person';
    default:
      return undefined;
  }
};

const mapJobTypeToIndeedJobType = (jobType?: string): IndeedJobType | undefined => {
  switch (jobType) {
    case 'FullTime':
    case 'Permanent':
      return 'Full-time';
    case 'PartTime':
      return 'Part-time';
    case 'Contract':
      return 'Contract';
    case 'Temporary':
      return 'Temporary';
    default:
      return undefined;
  }
};

const resolveIndeedJobTypes = (job: Partial<Job>): IndeedJobType[] | undefined => {
  const saved = job.indeedFields || {};
  const legacyJobType = (saved as IndeedFields & { jobType?: IndeedJobType }).jobType;

  if (Array.isArray(saved.jobTypes) && saved.jobTypes.length > 0) {
    return saved.jobTypes;
  }

  if (legacyJobType) {
    return [legacyJobType];
  }

  const mapped = mapJobTypeToIndeedJobType(job.jobType);
  return mapped ? [mapped] : undefined;
};

const toIndeedPayString = (value?: string | number): string | undefined => {
  if (value == null || value === '') {
    return undefined;
  }

  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value).replace(/,/g, ''));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed.toLocaleString('en-US') : undefined;
};

const parseSalaryRange = (salary?: string): { minimum?: number; maximum?: number } => {
  if (!salary) {
    return {};
  }

  const numbers = salary.match(/\d[\d,]*/g)?.map(value => Number(value.replace(/,/g, ''))) || [];

  if (numbers.length === 0) {
    return {};
  }

  if (numbers.length === 1) {
    return { minimum: numbers[0], maximum: numbers[0] };
  }

  return { minimum: Math.min(...numbers), maximum: Math.max(...numbers) };
};

const resolveIndeedShowPayBy = (
  saved: IndeedFields,
  salaryRange: { minimum?: number; maximum?: number },
): IndeedShowPayBy | undefined => {
  if (saved.showPayBy) {
    return saved.showPayBy;
  }

  if (
    salaryRange.minimum !== undefined &&
    salaryRange.maximum !== undefined &&
    salaryRange.minimum !== salaryRange.maximum
  ) {
    return 'Range';
  }

  if (salaryRange.minimum !== undefined) {
    return 'Exact amount';
  }

  return undefined;
};

export const buildIndeedFieldsFromJob = (job: Partial<Job>): IndeedFields => {
  const saved = job.indeedFields || {};
  const salaryRange = parseSalaryRange(job.salary);
  const showPayBy = resolveIndeedShowPayBy(saved, salaryRange);
  const jobLocationType = saved.jobLocationType ?? mapLocationTypeToIndeedLocationType(job.locationType);
  const requireRemoteResidence = indeedJobLocationTypeShowsRemoteResidenceQuestion(jobLocationType)
    ? saved.requireRemoteResidence
    : undefined;

  return {
    jobLocationType,
    requireRemoteResidence,
    jobLocation: indeedShowsJobLocation(jobLocationType, requireRemoteResidence)
      ? saved.jobLocation ?? job.location ?? ''
      : undefined,
    includeWillingToRelocate: indeedJobLocationTypeShowsRelocate(jobLocationType, requireRemoteResidence)
      ? saved.includeWillingToRelocate ?? false
      : false,
    hiringTimeline: saved.hiringTimeline,
    hiresNeeded: saved.hiresNeeded,
    jobTypes: resolveIndeedJobTypes(job),
    expectedHoursShowBy: saved.expectedHoursShowBy,
    expectedHoursAt: saved.expectedHoursAt,
    expectedHoursFrom: saved.expectedHoursFrom,
    expectedHoursTo: saved.expectedHoursTo,
    expectedHoursMaximum: saved.expectedHoursMaximum,
    expectedHoursMinimum: saved.expectedHoursMinimum,
    showPayBy,
    minimum: toIndeedPayString(saved.minimum) ?? (showPayBy === 'Range' ? toIndeedPayString(salaryRange.minimum) : undefined),
    maximum: toIndeedPayString(saved.maximum) ?? (showPayBy === 'Range' ? toIndeedPayString(salaryRange.maximum) : undefined),
    amount:
      toIndeedPayString(saved.amount) ??
      (indeedPayUsesAmount(showPayBy) ? toIndeedPayString(salaryRange.minimum ?? salaryRange.maximum) : undefined),
    rate:
      saved.rate ??
      (saved.minimum != null ||
      saved.maximum != null ||
      saved.amount != null ||
      salaryRange.minimum != null
        ? 'per year'
        : undefined),
    benefits: saved.benefits ?? [],
    payTransparencyExempt: saved.payTransparencyExempt,
  };
};

export const emptyIndeedFields = (): IndeedFields => ({
  jobLocation: '',
  includeWillingToRelocate: false,
});

export type IndeedAutofillPayload = IndeedFields & {
  jobId?: string;
  jobTitle: string;
  jobDescription?: string;
  clientName?: string;
};

export const buildIndeedAutofillPayload = (job: Partial<Job>): IndeedAutofillPayload => ({
  ...buildIndeedFieldsFromJob(job),
  jobId: job._id,
  jobTitle: job.title || '',
  jobDescription: job.description || '',
  clientName: job.clientName || '',
});
